// Network Simulation Engine with Proper Subnet Validation

export class NetworkSimulator {
  constructor() {
    this.devices = new Map();
    this.connections = new Map();
    this.arpCache = new Map();
  }

  addDevice(device) {
    this.devices.set(device.id, device);
  }

  removeDevice(deviceId) {
    this.devices.delete(deviceId);
    for (const [connId, conn] of this.connections) {
      if (conn.device1 === deviceId || conn.device2 === deviceId) {
        this.connections.delete(connId);
      }
    }
  }

  addConnection(device1Id, port1, device2Id, port2) {
    const connId = `${device1Id}-${port1}-${device2Id}-${port2}`;
    this.connections.set(connId, {
      id: connId,
      device1: device1Id,
      port1,
      device2: device2Id,
      port2,
      active: true,
    });
  }

  removeConnection(device1Id, port1, device2Id, port2) {
    const connId1 = `${device1Id}-${port1}-${device2Id}-${port2}`;
    const connId2 = `${device2Id}-${port2}-${device1Id}-${port1}`;
    
    this.connections.delete(connId1);
    this.connections.delete(connId2);
  }

  // Find which router port a device is connected to
  findConnectedRouterPort(deviceId, routerId) {
    for (const conn of this.connections.values()) {
      if (conn.device1 === deviceId && conn.device2 === routerId) {
        return conn.port2; // Router's port
      }
      if (conn.device2 === deviceId && conn.device1 === routerId) {
        return conn.port1; // Router's port
      }
    }
    return null;
  }

  // Get router interface for a specific port
  getRouterInterface(routerId, port) {
    const router = this.devices.get(routerId);
    if (!router || router.type !== 'router') return null;
    
    if (router.interfaces && router.interfaces.length > 0) {
      return router.interfaces.find(iface => iface.port === port);
    }
    
    return null;
  }

  areDirectlyConnected(device1Id, device2Id) {
    for (const conn of this.connections.values()) {
      if (
        (conn.device1 === device1Id && conn.device2 === device2Id) ||
        (conn.device1 === device2Id && conn.device2 === device1Id)
      ) {
        return conn;
      }
    }
    return null;
  }

  isSameSubnet(ip1, subnet1, ip2, subnet2) {
    if (!ip1 || !ip2 || !subnet1 || !subnet2) return false;
    
    const ip1Parts = ip1.split('.').map(Number);
    const ip2Parts = ip2.split('.').map(Number);
    const subnet1Parts = subnet1.split('.').map(Number);
    
    for (let i = 0; i < 4; i++) {
      if ((ip1Parts[i] & subnet1Parts[i]) !== (ip2Parts[i] & subnet1Parts[i])) {
        return false;
      }
    }
    return true;
  }

  isDevicePowered(deviceId) {
    const device = this.devices.get(deviceId);
    return device && device.powered;
  }

  // CRITICAL: Validate device can reach router through correct interface
  validateRouterAccess(deviceId, routerId) {
    const device = this.devices.get(deviceId);
    const router = this.devices.get(routerId);
    
    if (!device || !router || router.type !== 'router') {
      return { valid: false, error: 'Invalid device or router' };
    }

    // Find which port device is connected to
    const connectedPort = this.findConnectedRouterPort(deviceId, routerId);
    if (connectedPort === null) {
      return { valid: false, error: `${device.name} is not physically connected to ${router.name}` };
    }

    // Get the router interface for that port
    const routerInterface = this.getRouterInterface(routerId, connectedPort);
    if (!routerInterface || !routerInterface.ip) {
      return { valid: false, error: `Router port ${connectedPort + 1} has no IP configured` };
    }

    // CRITICAL CHECK: Device IP must be on same subnet as router interface
    if (!this.isSameSubnet(device.ip, device.subnet, routerInterface.ip, routerInterface.subnet)) {
      return { 
        valid: false, 
        error: `Subnet mismatch: ${device.name} (${device.ip}/${device.subnet}) is not on same subnet as router port ${connectedPort + 1} (${routerInterface.ip}/${routerInterface.subnet})`
      };
    }

    // Check gateway matches router interface
    if (device.gateway && device.gateway !== routerInterface.ip) {
      return {
        valid: false,
        error: `Gateway mismatch: ${device.name} gateway is ${device.gateway} but router port ${connectedPort + 1} is ${routerInterface.ip}`
      };
    }

    return { 
      valid: true, 
      port: connectedPort,
      interface: routerInterface
    };
  }

  // Enhanced route finding with strict subnet validation
  findRoute(sourceId, destIp) {
    const source = this.devices.get(sourceId);
    if (!source) return { error: 'Source device not found' };

    if (!source.powered) {
      return { error: 'Source device is powered off' };
    }

    const sourceIp = source.ip;
    if (!sourceIp) {
      return { error: 'Source IP not configured' };
    }

    // Check if destination is on the same subnet as source
    const isSameNet = this.isSameSubnet(
      sourceIp,
      source.subnet,
      destIp,
      source.subnet
    );

    if (isSameNet) {
      // Direct delivery on same network
      for (const device of this.devices.values()) {
        if (device.ip === destIp) {
          if (!device.powered) {
            return { error: 'Destination host unreachable (device powered off)' };
          }

          const conn = this.areDirectlyConnected(sourceId, device.id);
          if (conn) {
            return {
              path: [sourceId, device.id],
              nextHop: device.id,
              hops: 1,
            };
          } else {
            return this.findPathThroughSwitch(sourceId, device.id);
          }
        }
      }
    } else {
      // Need router - different network
      if (!source.gateway) {
        return { error: 'No route to host - gateway not configured' };
      }

      // Find router with matching interface
      let sourceRouter = null;
      let sourceRouterPort = null;

      for (const device of this.devices.values()) {
        if (device.type !== 'router' || !device.powered) continue;
        
        if (device.interfaces && device.interfaces.length > 0) {
          // Check each interface
          for (const iface of device.interfaces) {
            if (iface.ip === source.gateway) {
              sourceRouter = device;
              sourceRouterPort = iface.port;
              break;
            }
          }
        }
        
        if (sourceRouter) break;
      }

      if (!sourceRouter) {
        return { error: `Gateway unreachable - no router with interface IP ${source.gateway}` };
      }

      // CRITICAL: Validate source can actually access this router
      const validation = this.validateRouterAccess(sourceId, sourceRouter.id);
      if (!validation.valid) {
        return { error: validation.error };
      }

      return this.routeThroughRouter(sourceId, destIp, sourceRouter.id);
    }

    return { error: 'Destination host unreachable' };
  }

  findPathThroughSwitch(sourceId, destId) {
    const visited = new Set();
    const queue = [[sourceId]];

    const destDevice = this.devices.get(destId);
    if (!destDevice || !destDevice.powered) {
      return { error: 'Destination host unreachable (device powered off)' };
    }

    while (queue.length > 0) {
      const path = queue.shift();
      const current = path[path.length - 1];

      if (current === destId) {
        return {
          path,
          nextHop: path[1],
          hops: path.length - 1,
        };
      }

      if (visited.has(current)) continue;
      visited.add(current);

      if (current !== sourceId && current !== destId) {
        const currentDevice = this.devices.get(current);
        if (!currentDevice || !currentDevice.powered) {
          continue;
        }
      }

      for (const conn of this.connections.values()) {
        let next = null;
        if (conn.device1 === current) next = conn.device2;
        if (conn.device2 === current) next = conn.device1;

        if (next && !visited.has(next)) {
          const nextDevice = this.devices.get(next);
          if (nextDevice && (nextDevice.powered || next === destId)) {
            queue.push([...path, next]);
          }
        }
      }
    }

    return { error: 'No path found (check if switches/routers are powered on)' };
  }

  routeThroughRouter(sourceId, destIp, routerId) {
    const router = this.devices.get(routerId);
    
    if (!router || !router.powered) {
      return { error: 'Gateway unreachable (router powered off)' };
    }

    // Find destination device
    let destDevice = null;
    
    for (const device of this.devices.values()) {
      if (device.ip === destIp) {
        destDevice = device;
        break;
      }
    }

    if (!destDevice) {
      return { error: 'Destination host unreachable - IP not found' };
    }

    if (!destDevice.powered) {
      return { error: 'Destination host unreachable (device powered off)' };
    }

    // Find which router interface can reach the destination
    let destRouterPort = null;
    let destInterface = null;

    if (router.interfaces && router.interfaces.length > 0) {
      for (const iface of router.interfaces) {
        if (!iface.ip) continue;
        
        // Check if destination is on this interface's subnet
        if (this.isSameSubnet(destIp, destDevice.subnet, iface.ip, iface.subnet)) {
          destRouterPort = iface.port;
          destInterface = iface;
          break;
        }
      }
    }

    if (destRouterPort === null) {
      return { error: `Router has no interface in destination network (${destIp} subnet)` };
    }

    // CRITICAL: Validate destination can actually access router through this interface
    const validation = this.validateRouterAccess(destDevice.id, routerId);
    if (!validation.valid) {
      return { error: `Destination validation failed: ${validation.error}` };
    }

    // Verify it's the correct port
    if (validation.port !== destRouterPort) {
      return { 
        error: `Routing conflict: Destination should be on port ${destRouterPort + 1} but is connected to port ${validation.port + 1}`
      };
    }

    // Check if router can reach destination
    const routerToDestConn = this.areDirectlyConnected(routerId, destDevice.id);
    
    if (routerToDestConn) {
      return {
        path: [sourceId, routerId, destDevice.id],
        nextHop: routerId,
        hops: 2,
      };
    }

    // Try to find path through switches
    const pathResult = this.findPathThroughSwitch(routerId, destDevice.id);
    
    if (pathResult.path) {
      return {
        path: [sourceId, ...pathResult.path],
        nextHop: routerId,
        hops: pathResult.path.length,
      };
    }

    return { error: 'Cannot route to destination' };
  }

  performArp(deviceId, targetIp) {
    const device = this.devices.get(deviceId);
    if (!device) return null;

    const isSameNet = this.isSameSubnet(
      device.ip,
      device.subnet,
      targetIp,
      device.subnet
    );

    if (!isSameNet) {
      targetIp = device.gateway;
    }

    for (const dev of this.devices.values()) {
      if (dev.ip === targetIp) {
        if (!dev.powered) {
          return {
            ip: targetIp,
            mac: null,
            reachable: false,
          };
        }

        const route = this.findRoute(deviceId, targetIp);
        if (route.path) {
          return {
            ip: targetIp,
            mac: dev.mac,
            reachable: true,
          };
        }
      }
    }

    return {
      ip: targetIp,
      mac: null,
      reachable: false,
    };
  }

  ping(sourceId, destIp, callback) {
    const source = this.devices.get(sourceId);
    if (!source || !source.powered) {
      return { error: 'Source device not available' };
    }

    const sourceIp = source.ip;
    if (!sourceIp) {
      return { error: 'Source IP not configured' };
    }

    const route = this.findRoute(sourceId, destIp);
    
    if (route.error) {
      return { error: route.error };
    }

    const latency = Math.floor(Math.random() * 10) + 1;
    const result = {
      seq: 1,
      time: latency,
      ttl: 64 - route.hops,
    };

    if (callback) {
      callback(route.path, 1);
    }

    return {
      success: true,
      route: route.path,
      results: [result],
      packetsTransmitted: 1,
      packetsReceived: 1,
      packetLoss: 0,
    };
  }

  traceroute(sourceId, destIp) {
    const route = this.findRoute(sourceId, destIp);
    
    if (route.error) {
      return { error: route.error };
    }

    const hops = route.path.map((deviceId, index) => {
      const device = this.devices.get(deviceId);
      
      let displayIp = device.ip;
      if (device.type === 'router' && device.interfaces && device.interfaces.length > 0) {
        if (!displayIp && device.interfaces[0].ip) {
          displayIp = device.interfaces[0].ip;
        }
      }
      
      return {
        hop: index + 1,
        ip: displayIp || 'No IP',
        name: device.name,
        latency: Math.floor(Math.random() * 5) + index,
      };
    });

    return {
      success: true,
      hops,
    };
  }

  getRoutingTable(deviceId) {
    const device = this.devices.get(deviceId);
    if (!device) return [];

    const table = [];

    if (device.type === 'router' && device.interfaces) {
      device.interfaces.forEach(iface => {
        if (iface.ip) {
          const network = iface.ip.split('.').slice(0, 3).join('.') + '.0';
          table.push({
            destination: network,
            netmask: iface.subnet,
            gateway: '0.0.0.0',
            interface: iface.ip,
            metric: 1,
          });
        }
      });
      
      if (device.gateway) {
        table.push({
          destination: '0.0.0.0',
          netmask: '0.0.0.0',
          gateway: device.gateway,
          interface: device.interfaces[0]?.ip || device.ip,
          metric: 10,
        });
      }
    } else {
      if (device.gateway) {
        const network = device.ip.split('.').slice(0, 3).join('.') + '.0';
        table.push({
          destination: network,
          netmask: device.subnet,
          gateway: device.gateway,
          interface: device.ip,
          metric: 1,
        });

        table.push({
          destination: '0.0.0.0',
          netmask: '0.0.0.0',
          gateway: device.gateway,
          interface: device.ip,
          metric: 10,
        });
      } else {
        const network = device.ip.split('.').slice(0, 3).join('.') + '.0';
        table.push({
          destination: network,
          netmask: device.subnet,
          gateway: '0.0.0.0',
          interface: device.ip,
          metric: 1,
        });
      }
    }

    return table;
  }

  getArpTable(deviceId) {
    const device = this.devices.get(deviceId);
    if (!device) return [];

    const arpTable = [];

    for (const otherDevice of this.devices.values()) {
      if (otherDevice.id === deviceId) continue;
      if (!otherDevice.powered) continue;

      const isSameNet = this.isSameSubnet(
        device.ip,
        device.subnet,
        otherDevice.ip,
        device.subnet
      );

      if (isSameNet) {
        const route = this.findRoute(deviceId, otherDevice.ip);
        if (route.path) {
          arpTable.push({
            ip: otherDevice.ip,
            mac: otherDevice.mac,
            type: 'dynamic',
          });
        }
      }
    }

    if (device.gateway) {
      const gateway = Array.from(this.devices.values()).find(d => {
        if (d.ip === device.gateway) return true;
        if (d.type === 'router' && d.interfaces) {
          return d.interfaces.some(iface => iface.ip === device.gateway);
        }
        return false;
      });
      
      if (gateway && gateway.powered) {
        arpTable.push({
          ip: device.gateway,
          mac: gateway.mac,
          type: 'dynamic',
        });
      }
    }

    return arpTable;
  }
}
